import types from '../mutation-types';
import ConversationApi from '../../api/inbox/conversation';
import CustomViewsAPI from '../../api/customViews';
import * as Sentry from '@sentry/vue';

// A count refresh sends one request per inbox or per view. A cache revalidation
// repeats the same inboxes, and that repeat waits at least this long.
export const MIN_REFRESH_INTERVAL_MS = 2500;

// Identifies the input of a refresh. Two refreshes of the same set are the same
// work; a set with one inbox more is not.
const idsKey = items =>
  items
    .map(item => item.id)
    .sort((a, b) => a - b)
    .join(',');

// Outside the store: the newest input that waits for the refresh in flight.
const queuedItems = { inboxes: null, views: null };

const state = {
  inboxes: {},
  views: {},
  updatedOn: { inboxes: null, views: null },
  // input of the refresh in flight, null while none runs
  fetching: { inboxes: null, views: null },
  // input of the last finished refresh
  fetchedKey: { inboxes: null, views: null },
  // localStorage key the view counts were restored from
  viewsStorageKey: null,
};

// Refreshes the counts of one kind. A refresh never runs beside another refresh
// of the same kind, and it never drops an input that changed: that input carries
// an inbox or a view with no count.
async function refreshKind(commit, $state, kind, items, fetchMeta) {
  const key = idsKey(items);
  const pendingKey = queuedItems[kind]
    ? idsKey(queuedItems[kind])
    : $state.fetching[kind];

  // The same refresh already runs or waits.
  if (pendingKey === key) return;

  // Another refresh of this kind runs. Do this one when that one ends.
  if (pendingKey !== null) {
    queuedItems[kind] = items;
    return;
  }

  // The input did not change, and the counts are fresh.
  if (
    key === $state.fetchedKey[kind] &&
    new Date() - $state.updatedOn[kind] < MIN_REFRESH_INTERVAL_MS
  ) {
    return;
  }

  try {
    let current = items;
    while (current) {
      const currentKey = idsKey(current);
      queuedItems[kind] = null;
      commit(types.SET_SIDEBAR_META_FETCHING, { [kind]: currentKey });
      // The refreshes of one kind must run in sequence.
      // eslint-disable-next-line no-await-in-loop
      const meta = await fetchMeta(current);
      commit(types.SET_SIDEBAR_META, { [kind]: meta, key: currentKey });
      current = queuedItems[kind];
    }
  } finally {
    queuedItems[kind] = null;
    commit(types.SET_SIDEBAR_META_FETCHING, { [kind]: null });
  }
}

export const getters = {
  getStats: $state => $state,
};

async function fetchInboxes(inboxes) {
  const res = Promise.all(
    inboxes.map(async inbox => {
      try {
        const params = { inboxId: inbox.id };
        const response = await ConversationApi.meta(params);
        const {
          data: { meta: inboxMeta },
        } = response;
        return { [inbox.id]: inboxMeta };
      } catch (error) {
        Sentry.captureException(error);
        return {};
      }
    })
  );
  return res.then(results => Object.assign({}, ...results));
}

// Views counted per counts request. An account can share hundreds of views, and
// one request per view sent all of them at once on every refresh. The server
// counts the views of one request one after another, so a batch stays small and
// the batches run in parallel: on 250 views in production a count took 70 ms,
// and a batch of 10 took at most 1.3 s (25 requests), where a batch of 25 took
// 2.6 s.
export const VIEWS_PER_REQUEST = 10;

// The expiries of Conversations::CountCache (app/services/conversations/count_cache.rb)
// by the last count: a large count moves by little.
const countCacheExpiry = count => {
  if (count > 10000) return 10 * 60 * 1000;
  if (count > 1000) return 5 * 60 * 1000;
  return 5 * 1000;
};

// How long a view count stays fresh, by its last value: a quarter of its cache
// expiry, so the sidebar picks up a recount soon after the cache has one. A small
// count is refetched on every poll.
export const viewRefreshInterval = count => countCacheExpiry(count) / 4;

// After an edit the store holds the filter in the key order the server echoed,
// after a page load in the order Postgres keeps. Both are the same filter.
const sortedKeysJSON = value =>
  JSON.stringify(value, (_key, item) =>
    item && typeof item === 'object' && !Array.isArray(item)
      ? Object.fromEntries(
          Object.keys(item)
            .sort()
            .map(key => [key, item[key]])
        )
      : item
  );

// A view keeps its id when its filter is edited, and the count of the old filter
// must not wait out its interval. A count remembers the filter it counted.
const viewQueryKey = view => {
  const text = sortedKeysJSON(view.query?.payload ?? []);
  let hash = 5381;
  for (let i = 0; i < text.length; i += 1) {
    hash = (hash * 33 + text.charCodeAt(i)) % 4294967296;
  }
  return `${text.length}:${hash.toString(36)}`;
};

const isViewDue = (view, entry, now) =>
  !entry?.fetched_at ||
  entry.query_key !== viewQueryKey(view) ||
  now - entry.fetched_at >= viewRefreshInterval(entry.all_count);

// The view counts of the last refresh outlive the page: a new page shows them at
// once, and refetches each one when its own interval ends, not all at once.
// Logout clears localStorage, and the counts depend on the user, so the key
// names the account and the user.
const viewsStorageKey = (accountId, userId) =>
  accountId && userId ? `sidebarStats::views::${accountId}::${userId}` : null;

const readStoredViews = key => {
  try {
    const views = JSON.parse(window.localStorage.getItem(key));
    return views && typeof views === 'object' ? views : {};
  } catch (error) {
    return {};
  }
};

const writeStoredViews = (key, views) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(views));
  } catch (error) {
    // A full or blocked storage only costs the counts of the next page.
  }
};

async function fetchViewsChunk(views) {
  try {
    const {
      data: { meta },
    } = await CustomViewsAPI.getCounts(views.map(view => view.id));
    return meta;
  } catch (error) {
    // Silently ignore: these views stay due and are refetched on the next poll.
    return {};
  }
}

// Refetches the views whose count is due and keeps the others. A view that got
// no count keeps the one it had, and stays due.
async function fetchViews(views, previous, storageKey) {
  if (!views || views.length === 0 || views[0].filter_type === 'contact')
    return {};
  const now = Date.now();
  const due = views.filter(view => isViewDue(view, previous[view.id], now));
  const chunks = [];
  for (let i = 0; i < due.length; i += VIEWS_PER_REQUEST) {
    chunks.push(due.slice(i, i + VIEWS_PER_REQUEST));
  }
  const fetched = Object.assign(
    {},
    ...(await Promise.all(chunks.map(fetchViewsChunk)))
  );

  const counts = {};
  views.forEach(view => {
    if (fetched[view.id]) {
      counts[view.id] = {
        ...fetched[view.id],
        fetched_at: now,
        query_key: viewQueryKey(view),
      };
    } else if (previous[view.id]) {
      counts[view.id] = previous[view.id];
    }
  });
  if (storageKey) writeStoredViews(storageKey, counts);
  return counts;
}

export const actions = {
  get: async ({ commit, state: $state, rootGetters }, { inboxes, views }) => {
    if (inboxes) {
      await refreshKind(commit, $state, 'inboxes', inboxes, fetchInboxes);
    }
    if (views) {
      const storageKey = viewsStorageKey(
        rootGetters.getCurrentAccountId,
        rootGetters.getCurrentUserID
      );
      if (storageKey && storageKey !== $state.viewsStorageKey) {
        commit(types.RESTORE_SIDEBAR_VIEWS, {
          key: storageKey,
          views: readStoredViews(storageKey),
        });
      }
      await refreshKind(commit, $state, 'views', views, current =>
        fetchViews(current, $state.views, storageKey)
      );
    }
  },
};

export const mutations = {
  [types.SET_SIDEBAR_META]($state, { inboxes, views, key = null }) {
    if (inboxes) {
      $state.inboxes = inboxes;
      $state.updatedOn.inboxes = new Date();
      $state.fetchedKey.inboxes = key;
    }
    if (views) {
      $state.views = views;
      $state.updatedOn.views = new Date();
      $state.fetchedKey.views = key;
    }
  },
  [types.SET_SIDEBAR_META_FETCHING]($state, keys) {
    $state.fetching = { ...$state.fetching, ...keys };
  },
  [types.RESTORE_SIDEBAR_VIEWS]($state, { key, views }) {
    $state.views = views;
    $state.viewsStorageKey = key;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
